import _ from 'lodash';
import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-modal';

import { lookupDepartmentById, lookupUserById } from '../../actions/lookups';
import {
  getDateTime,
  getSecondsFromTimeRange,
  getSecondsFromTimeRange_24_5,
  getSecondsFromTimeRange_24_6,
  returnTabAge_24_5,
  returnTabAge_24_6,
  returnTabAge_24_7,
  convertSecondsToDays,
} from '../../utils/dateFunctions';

// Helper function to escape CSV values
const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return `"${stringValue}"`;
};

const DownloadCSV = (props) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [onlyLastNote, setOnlyLastNote] = useState(false);
  const [includeAllColumns, setIncludeAllColumns] = useState(false);
  const [removeEmptyColumns, setRemoveEmptyColumns] = useState(false);
  const dispatch = useDispatch();

  const dealership_columns = useSelector((state) => state.settings_departments.dealership_columns || []);
  const current_department = useSelector((state) => state.settings_departments.department || {});
  const department_properties = current_department.department_properties || [];
  const extra_props = department_properties.map((dept_prop) => dept_prop.vehicle_column.name);
  const work_flows = useSelector((state) => state.settings_departments.work_flows);
  const active_vehicles = useSelector((state) => state.settings_refresh.active_vehicles);
  const dealer_settings = useSelector((state) => state.settings_site.dealer_settings);
  const installation = useSelector((state) => state.settings_dealerships.installation);

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => {
    setModalIsOpen(false);
    setOnlyLastNote(false);
    setIncludeAllColumns(false);
    setRemoveEmptyColumns(false);
  };

  const dealerSettingValue = useMemo(() => (name) => {
    const value = _.sortBy(
      _.filter(dealer_settings, (o) => o.key === name && o.dealership === installation.subdomain),
      'id'
    );
    if (value.length === 0) {
      return _.sortBy(
        _.filter(dealer_settings, (o) => o.key === name && (o.dealership === '' || o.dealership === null || o.dealership === undefined)),
        'id'
      )[0]?.value || '';
    }
    return value[0]?.value || '';
  }, [dealer_settings, installation.subdomain]);

  const downloadCSV = (vehicles, includeAll = false) => {
    const end = new Date();
    // Default headers
    let headers2 = [
      'department',
      'dealership',
      'location',
      'days in bucket',
      'total age (days)',
      'stock',
      'vin',
      'year',
      'make',
      'model',
      'color',
      'miles',
      'certified',
      ...extra_props,
      'notes',
    ];

    // Add all dealership columns if includeAllColumns is checked
    if (includeAll) {
      headers2 = [...headers2, ...dealership_columns];
    }

    // Ensure unique headers
    headers2 = [...new Set(headers2)];

    // Build CSV rows and track column values
    const full_csv = [headers2.map(escapeCsvValue)];
    const columnValues = headers2.reduce((acc, col) => ({ ...acc, [col]: [] }), {});
    const work_flow = _.find(work_flows, { name: 'used' }) || {};
    const work_flow_properties = work_flow.work_flow_properties?.length > 0 ? work_flow.work_flow_properties : [];

    vehicles.forEach((vehicle) => {
      const { completed_departments } = vehicle || {};
      const row = [];
      const department = vehicle.department_id ? dispatch(lookupDepartmentById(vehicle.department_id)) : {};
      const deptName = (department.display_name || department.name || '').replace(/[#{}":]/g, ' ').trim();

      headers2.forEach((column) => {
        let value = '';
        let seconds = 0;
        let plus_seconds = 0;
        switch (column) {
          case 'department':
            value = deptName;
            break;
          case 'dealership':
            value = vehicle.dealership;
            break;
          case 'location':
            value = vehicle.subdomain;
            break;
          case 'notes':
            value = Array.isArray(vehicle.vehicle_notes) && vehicle.vehicle_notes.length > 0
              ? onlyLastNote
                ? (() => {
                    const lastNote = vehicle.vehicle_notes[vehicle.vehicle_notes.length - 1];
                    const user = lastNote.uid ? dispatch(lookupUserById(lastNote.uid)) : null;
                    return `${user?.user_name || 'User N/A'} - ${(lastNote.value || '').replace(/[#"]/g, '')}`.trim();
                  })()
                : vehicle.vehicle_notes
                    .map((note) => {
                      const user = note.uid ? dispatch(lookupUserById(note.uid)) : null;
                      return `${user?.user_name || 'User N/A'} - ${(note.value || '').replace(/[#"]/g, '')}`.trim();
                    })
                    .join(' | ')
              : '';
            break;
          case 'days in bucket':
          case 'total age (days)':
            const time_tab = column === 'days in bucket' ? 'first_time_tab' : 'second_time_tab';
            const tabSetting = dealerSettingValue(time_tab);
            switch (tabSetting) {
              case 'age-24_7':
                seconds = getSecondsFromTimeRange(vehicle.created_at, end);
                break;
              case 'age-24_6':
                seconds = getSecondsFromTimeRange_24_6(vehicle.created_at, end);
                break;
              case 'age-24_5':
                seconds = getSecondsFromTimeRange_24_5(vehicle.created_at, end);
                break;
              case 'age-recononly-24_7':
                plus_seconds = getSecondsFromTimeRange(vehicle.department_time, end);
                seconds = returnTabAge_24_7(completed_departments, work_flow_properties);
                break;
              case 'age-recononly-24_6':
                plus_seconds = getSecondsFromTimeRange_24_6(vehicle.department_time, end);
                seconds = returnTabAge_24_6(completed_departments, work_flow_properties);
                break;
              case 'age-recononly-24_5':
                plus_seconds = getSecondsFromTimeRange_24_5(vehicle.department_time, end);
                seconds = returnTabAge_24_5(completed_departments, work_flow_properties);
                break;
              case 'department-24_7':
                seconds = getSecondsFromTimeRange(vehicle.department_time, end);
                break;
              case 'department-24_6':
                seconds = getSecondsFromTimeRange_24_6(vehicle.department_time, end);
                break;
              case 'department-24_5':
                seconds = getSecondsFromTimeRange_24_5(vehicle.department_time, end);
                break;
              case 'department-instance-24_7':
                seconds = getSecondsFromTimeRange(vehicle.department_time, end);
                break;
              case 'department-instance-24_6':
                seconds = getSecondsFromTimeRange_24_6(vehicle.department_time, end);
                break;
              case 'department-instance-24_5':
                seconds = getSecondsFromTimeRange_24_5(vehicle.department_time, end);
                break;
              default:
                seconds = getSecondsFromTimeRange(vehicle.department_time, end);
            }
            value = convertSecondsToDays(seconds + plus_seconds);
            break;
          default:
            value = vehicle[column] || _.find(vehicle.vehicle_properties, (o) => o.key === column)?.value || '';
        }
        row.push(escapeCsvValue(value));
        columnValues[column].push(value);
      });
      full_csv.push(row);
    });

    // Handle empty columns
    let final_csv = full_csv;
    if (removeEmptyColumns) {
      const mandatoryFields = [
        'department',
        'dealership',
        'location',
        'days in bucket',
        'total age (days)',
        'stock',
        'vin',
        'year',
        'make',
        'model',
        'color',
        'miles',
        'certified',
        'notes',
      ];
      const filteredHeaders = headers2.filter((col) => {
        // Keep mandatory fields
        if (mandatoryFields.includes(col)) return true;
        // Keep columns with at least one non-empty value
        return columnValues[col].some((val) => val !== '' && val != null);
      });
      // Rebuild CSV with filtered headers
      final_csv = [filteredHeaders.map(escapeCsvValue)];
      full_csv.slice(1).forEach((row) => {
        const filteredRow = filteredHeaders.map((col) => row[headers2.indexOf(col)]);
        final_csv.push(filteredRow);
      });
    }

    const filename = `${current_department.name || 'export'}_${getDateTime()}.csv`;
    const bom = '\uFEFF';
    const csvString = final_csv.map((row) => row.join(',')).join('\n');
    const csvContent = `${bom}${csvString}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onDownloadButtonClick = () => {
    const end = new Date();
    let vehicles = active_vehicles || [];
    vehicles = props.sort_descending
      ? _.sortBy(vehicles, props.sort_by_data).reverse()
      : _.sortBy(vehicles, props.sort_by_data);
    downloadCSV(vehicles, includeAllColumns);
    closeModal();
  };

  return (
    <div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="DownloadCSV"
        className="d-flex justify-content-center pad-bottom-2 space-bottom-2"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="bg-light col-md-6 md-auto space-top-2 pad-top-1 pad-bottom-1 pad-left-2 pad-right-2">
          <div className="modal-content">
            <div className="container">
              <h4 className="modal-title d-inline">Download CSV</h4>
              <button className="btn btn-sm btn-danger float-end" onClick={closeModal}>Close</button>
            </div>
            <div className="modal-body">
              <div className="card-block bg-white pad-left-1 pad-right-1">
                <div className="d-flex flex-column">
                  <span className="switch mb-2 pad-top-1">
                    <input
                      type="checkbox"
                      id="switch_id_onlyLastNote"
                      checked={onlyLastNote}
                      onChange={(e) => setOnlyLastNote(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label className="text-capitalize" htmlFor="switch_id_onlyLastNote">
                      Only include last note
                    </label>
                  </span>
                  <span className="switch mb-2 pad-top-2" style={{ marginLeft: '0px' }}>
                    <input
                      type="checkbox"
                      id="switch_id_includeAllColumns"
                      checked={includeAllColumns}
                      onChange={(e) => setIncludeAllColumns(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label className="text-capitalize" htmlFor="switch_id_includeAllColumns">
                      Include All Columns
                    </label>
                  </span>
                  <span className="switch mb-2 pad-top-2" style={{ marginLeft: '0px' }}>
                    <input
                      type="checkbox"
                      id="switch_id_removeEmptyColumns"
                      checked={removeEmptyColumns}
                      onChange={(e) => setRemoveEmptyColumns(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label className="text-capitalize" htmlFor="switch_id_removeEmptyColumns">
                      Remove Empty Columns
                    </label>
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer text-center">
              <button className="btn btn-primary" onClick={onDownloadButtonClick}>
                <i className="fa fa-download" /> Download {current_department.name}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <span className="btn btn-light btn-md" onClick={openModal}>
        <i className="fa fa-download" />
      </span>
    </div>
  );
};

export default DownloadCSV;